import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { parseISO } from "date-fns";
import { useFeePostingTimeRangesList } from "gen/endpoints/charge/charge";
import { useFeeChargeEventCreate } from "gen/endpoints/fee-charge-event/fee-charge-event";
import { useFeeListSuspense } from "gen/endpoints/fees/fees";
import { FeeChargeTimingEnum } from "gen/model/feeChargeTimingEnum";
import { FeeData } from "gen/model/feeData";
import { map } from "lodash-es";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import { LoadingState } from "shared/components/empty/LoadingState";
import Form from "shared/components/form/Form";
import { Select } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { ConfirmationDialogProps } from "shared/components/modal/confirm/ConfirmationDialog";
import { WarningIcon } from "shared/icons/WarningIcon";
import { formatIsoDateToSummary } from "shared/utils/DateUtils";

export interface PostFeesModalData {
  feeChargeEventId: string;
}

export interface PostFeesModalProps {
  fundFamilyId: string;
  lastChargedPeriod?: string | null;
}

export const PostFeesModal: React.FC<
  ConfirmationDialogProps<PostFeesModalData, PostFeesModalProps>
> = ({ alertProps, ...props }) => {
  const { t } = useTranslation(["fees"]);
  return (
    <Modal scrollBehavior="inside" {...alertProps}>
      <ModalOverlay />
      <ModalContent>
        <QueryBoundary
          pendingFallback={
            <>
              <ModalHeader>
                {t("fees:create.title")}
                <ModalCloseButton />
              </ModalHeader>
              <ModalBody>
                <LoadingState />
              </ModalBody>
            </>
          }
        >
          <PostFeesModalContent {...props} />
        </QueryBoundary>
      </ModalContent>
    </Modal>
  );
};

const PostFeesModalContent: React.FC<
  Omit<
    ConfirmationDialogProps<PostFeesModalData, PostFeesModalProps>,
    "alertProps"
  >
> = ({
  onSuccess,
  onCancel,
  extraProps: { fundFamilyId, lastChargedPeriod },
}) => {
  const { t } = useTranslation(["common", "fees"]);

  const { data: feesData } = useFeeListSuspense(fundFamilyId);

  const [selectedFee, setSelectedFee] = useState<FeeData | undefined>(
    feesData[0],
  );

  const { data: suggestedTimeRangeData } = useFeePostingTimeRangesList(
    fundFamilyId,
    selectedFee?.id || "",
    { option_count: 1 },
    {
      query: {
        enabled: !!selectedFee,
      },
    },
  );

  const selectedTimeRange = suggestedTimeRangeData?.[0];
  const feeFullyCharged =
    suggestedTimeRangeData && suggestedTimeRangeData.length === 0;

  const { mutateAsync: feeChargeEventCreate, isSuccess } =
    useFeeChargeEventCreate();

  const findAndSelectFee = (value: string) => {
    const fee = feesData.find((fee) => fee.id === value);
    setSelectedFee(fee);
  };

  const firstDayOfCurrentPeriod = selectedTimeRange?.charged_date_lower;
  const firstDayOfNextPeriod =
    selectedTimeRange && parseISO(selectedTimeRange.charged_date_upper);
  const lastDayOfCurrentPeriod =
    firstDayOfNextPeriod &&
    new Date(
      firstDayOfNextPeriod.setDate(firstDayOfNextPeriod.getDate() - 1),
    ).toISOString();
  const effectiveDate =
    firstDayOfCurrentPeriod &&
    lastDayOfCurrentPeriod &&
    formatIsoDateToSummary(
      selectedFee?.charge_timing === FeeChargeTimingEnum.in_advance
        ? firstDayOfCurrentPeriod
        : lastDayOfCurrentPeriod,
    );

  const onSubmit = async () => {
    const feeChargeEvent = await feeChargeEventCreate({
      fundFamilyId,
      data: {
        fund_family_id: fundFamilyId,
        fee_id: selectedFee!.id,
        charged_date_lower: selectedTimeRange?.charged_date_lower ?? "",
      },
    });
    const { id: feeChargeEventId } = feeChargeEvent;
    if (!feeChargeEventId) {
      return;
    }
    onSuccess({ feeChargeEventId });
  };

  const form = useForm();

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      // keep form disabled after success while navigating
      isDisabled={isSuccess}
    >
      <ModalHeader>
        {t("fees:create.title")}
        <ModalCloseButton />
      </ModalHeader>
      <ModalBody>
        <Select
          title={t("fees:create.fee")}
          value={selectedFee?.id || ""}
          onChangeValue={findAndSelectFee}
          isReadOnly={feesData.length === 1}
        >
          {feesData.map((fee) => (
            <SelectOption key={fee.id} value={fee.id}>
              {fee.name}
            </SelectOption>
          ))}
        </Select>
        {suggestedTimeRangeData && suggestedTimeRangeData.length > 0 && (
          <Select
            title={t("fees:create.availableTimeRange")}
            elements={map(suggestedTimeRangeData, (data) => data.option)}
            value={selectedTimeRange?.option.value || ""}
            isReadOnly={true}
          />
        )}
        {feeFullyCharged && (
          <Alert status="warning" data-ls-enabled={true} data-public={true}>
            <AlertIcon>
              <WarningIcon />
            </AlertIcon>
            <AlertDescription>
              {t("fees:postFees.feeExhausted", {
                rich_name_of_period: lastChargedPeriod,
              })}
            </AlertDescription>
          </Alert>
        )}
        {selectedFee?.charge_timing && effectiveDate && (
          <Box borderTopWidth="1px" paddingTop="16px" alignItems="flex-start">
            <Trans
              ns="fees"
              i18nKey="create.info"
              values={{
                charge_timing: t(
                  `fees:chargeTiming.${selectedFee.charge_timing}`,
                ),
                effective_date: effectiveDate,
              }}
              components={[
                <Text key="effectiveDateSpan" as="span" fontWeight="500" />,
              ]}
            />
          </Box>
        )}
      </ModalBody>
      <ModalFooter gap={2}>
        <ButtonOrder
          dismiss={<FormCancelButton onClick={() => onCancel()} />}
          confirm={
            <FormSubmitButton isDisabled={!selectedTimeRange}>
              {t("fees:create.continueToReviewBasis")}
            </FormSubmitButton>
          }
        />
      </ModalFooter>
    </Form>
  );
};
